import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { AssemblyLine, Bootstrap, DirectoryUser, Model, Reason, Shift } from '@clear/contracts';
import { currentShift, type Gate, type Role } from '@clear/domain';
import { api } from '../lib/api';
import { storage } from '../lib/storage';
import { now, syncServerTime } from '../lib/time';
import { useI18n } from '../i18n';

export const BOOTSTRAP_KEY = ['bootstrap'] as const;

export function useBootstrap() {
  return useQuery({
    queryKey: BOOTSTRAP_KEY,
    queryFn: async () => {
      const sent = Date.now();
      const b = await api<Bootstrap>('/bootstrap');
      syncServerTime(b.serverTime, sent);
      return b;
    },
    staleTime: 5 * 60_000,
    networkMode: 'offlineFirst',
  });
}

export const ROLE_COLOUR: Record<Role, string> = {
  planner: '#1C3D5A',
  paint: '#9A5B24',
  rm: '#2F6B6E',
  quality: '#6A4A92',
  supervisor: '#2D5FC4',
  mgmt: '#3B4A44',
  admin: '#5F6B66',
};

export interface Lookups {
  b: Bootstrap;
  tz: string;
  model: (id: string) => Model;
  reason: (id: string | null | undefined) => Reason | undefined;
  reasonLabel: (id: string | null | undefined) => string;
  user: (id: string | null | undefined) => DirectoryUser | undefined;
  userName: (id: string | null | undefined) => string;
  /** Who owns a gate (for "who to call"): the first active user holding only that role, else any. */
  owner: (gate: Gate | 'supervisor') => DirectoryUser | undefined;
  reasonsFor: (gate: Gate) => Reason[];
  shift: (id: string) => Shift | undefined;
  activeLines: AssemblyLine[];
  line: (id: string) => AssemblyLine | undefined;
}

const UNKNOWN_MODEL: Model = {
  id: '?',
  name: '—',
  sweepMm: 0,
  finish: '',
  colourHex: '#cccccc',
  blades: 3,
  photoUrl: null,
  active: false,
  useCount: 0,
};

export function useLookups(): Lookups | null {
  const { data } = useBootstrap();
  const { lang } = useI18n();
  return useMemo(() => {
    if (!data) return null;
    const models = new Map(data.models.map((m) => [m.id, m]));
    const reasons = new Map(data.reasons.map((r) => [r.id, r]));
    const users = new Map(data.directory.map((u) => [u.id, u]));
    const shifts = new Map(data.shifts.map((s) => [s.id, s]));
    const lines = new Map(data.lines.map((l) => [l.id, l]));
    return {
      b: data,
      tz: data.plant.timezone,
      model: (id) => models.get(id) ?? { ...UNKNOWN_MODEL, id },
      reason: (id) => (id ? reasons.get(id) : undefined),
      reasonLabel: (id) => {
        const r = id ? reasons.get(id) : undefined;
        return r ? (lang === 'hi' ? r.labelHi : r.labelEn) : '';
      },
      user: (id) => (id ? users.get(id) : undefined),
      userName: (id) => (id ? (users.get(id)?.name ?? '—') : '—'),
      owner: (gate) => {
        const active = data.directory.filter((u) => u.active && u.roles.includes(gate));
        return active.find((u) => u.roles.length === 1) ?? active[0];
      },
      reasonsFor: (gate) =>
        data.reasons.filter((r) => r.gate === gate && r.active).sort((a, b) => a.sort - b.sort),
      shift: (id) => shifts.get(id),
      activeLines: data.lines.filter((l) => l.active),
      line: (id) => lines.get(id),
    };
  }, [data, lang]);
}

/** The assembly line this device looks at (remembered per device; default: first active line). */
export function useSelectedLine(lk: Lookups | null): [AssemblyLine | undefined, (id: string) => void] {
  const saved = storage.get('clear.line');
  const line = lk ? (lk.activeLines.find((l) => l.id === saved) ?? lk.activeLines[0]) : undefined;
  return [line, (id: string) => storage.set('clear.line', id)];
}

export function useCurrentShift(lk: Lookups | null) {
  return useMemo(() => {
    if (!lk) return null;
    return currentShift(
      lk.b.shifts.filter((s) => s.active),
      now(),
      lk.tz,
    );
  }, [lk]);
}
